/**
 * @fileoverview Org portal — everything the organization is teaching.
 *
 * Every class meeting across every program, coloured by program so a week
 * reads at a glance. The colours are fixed and cycled rather than random: a
 * calendar whose colours move between page loads is worse than one with none.
 *
 * **Three views, because an org asks three questions.** List answers "what is
 * on, in order"; the week grid answers "who is teaching at four o'clock", which
 * a stacked list cannot — an organization's Tuesday overlaps by design, and the
 * old single week list simply printed simultaneous classes one under another as
 * though they were sequential. Month answers "what does this term look like".
 * All three are MeetingCalendar, the same component one class's own calendar
 * uses, so the two never drift apart.
 *
 * **A private-lessons program is named, not omitted.** Its schedule is
 * bookings in Scheduler, which Hans cannot enumerate — and a calendar that
 * silently leaves a whole program out reads as "that program has nothing on",
 * which is a different and wrong claim.
 *
 * Filters narrow what is shown and say so, the same discipline as the roster:
 * a week that looks empty because of a filter must not be mistaken for a
 * quiet week.
 *
 * **Scheduling preferences** opens Settings → Schedule, where the Kalver
 * Scheduler connection lives: the calendar is where an administrator notices
 * something about scheduling, so the way to change it starts here too.
 *
 * **Pressing a meeting opens its details** (MeetingDetailsDialog): program,
 * class, when, who, where. A meeting of a term Hans holds can be moved or
 * cancelled from there, through the class's own revisioned meeting save; a
 * connected class's meetings are Scheduler's and say so. "Open class" goes to
 * the class page with that meeting open, where its prep note and plan live.
 *
 * **A cover can be named right there** (C3, decided Nov 1, 2026): a Hans
 * meeting that is not cancelled and has no substitute offers Assign
 * substitute beside Open class, and one nobody is down to teach says "No
 * instructor" and leads with it. A covered meeting's cover is changed on its
 * class page, which knows who it is.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { zoneAbbreviation } from "@/lib/schedule";
import { Button, Card, PageShell, SectionHeader } from "@/components/studio/primitives";
import MeetingCalendar, { SWATCH, rangeFor, weekStart } from "@/components/org/MeetingCalendar";
import MeetingDetailsDialog, { calendarMeeting } from "@/components/org/MeetingDetailsDialog";
import AssignSubstituteDialog from "@/components/org/AssignSubstituteDialog";
import { ORG_SCHEDULE_SETTINGS_PATH } from "@/lib/orgSettings";

/** The viewer's own zone: what an unlabelled time reads as when the
    organization has none of its own. */
/** Everyone a meeting names as teaching it: the class's instructor ledger
 * (`instructor_names`), so a filter by one co-teacher keeps the class, or
 * `instructor_name` from a payload that predates it. */
export const teachersOf = (m) => (m.instructor_names?.length ? m.instructor_names : [m.instructor_name]).filter(Boolean);

/** A meeting a cover can be named for from the calendar: Hans's, not cancelled, with none yet. */
export const canCover = (m) => Boolean(m?.class_id) && m.status !== "cancelled" && !m.substitute_name && !m.session_id;

const viewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const filterClass =
  "rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none focus:border-[var(--accent-yellow)]";

export default function OrgCalendarPage() {
  const navigate = useNavigate();
  // List by default, like the instructor calendar: the two portals are one
  // product, and an administrator who wants the overlap picture asks for it.
  const [view, setView] = useState("list");
  const [anchor, setAnchor] = useState(() => weekStart(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programFilter, setProgramFilter] = useState("all");
  const [instructorFilter, setInstructorFilter] = useState("all");
  // The meeting whose details are open, as the server sent it.
  const [picked, setPicked] = useState(null);
  // The meeting a cover is being named for, once its details have closed.
  const [covering, setCovering] = useState(null);

  // The month grid spills into the neighbouring months, so the range follows
  // the VIEW rather than the anchor alone — otherwise the first and last rows
  // of a month render empty and read as "nothing on".
  const { start, end } = rangeFor(view, anchor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orgApi.get(`/org/calendar?start=${start}&end=${end}`);
      setData(res.data);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load the calendar"));
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const all = data?.meetings || [];
    return all.filter(
      (m) =>
        (programFilter === "all" || m.program_id === programFilter) &&
        (instructorFilter === "all" || teachersOf(m).includes(instructorFilter)),
    );
  }, [data, programFilter, instructorFilter]);

  /* The org's meetings as the shared calendar takes them. WHO is teaching goes
     in the subtitle rather than the title: an administrator scanning a Tuesday
     is looking for a person as often as for a class, and the class name alone
     made them click every block to find out. */
  const ownZone = data?.timezone || viewerZone();
  const events = useMemo(
    () =>
      shown.map((m) => ({
        // A connected class's meeting has Scheduler's own id, which survives it
        // being moved; a legacy one is identified by where it sits.
        id: m.session_id || `${m.class_id}-${m.date}-${m.time_local}`,
        date: m.date,
        time_local: m.time_local,
        // Every meeting says the zone its time is in. The calendar's own zone
        // (the organization's, named once in the header; else the viewer's)
        // goes unsaid, so only a meeting in another zone is labelled: an
        // unqualified "4:00" in Pacific time would be read as Eastern.
        timezone: m.timezone && m.timezone !== ownZone ? zoneAbbreviation(m.timezone, m.date) : undefined,
        duration_minutes: m.duration_minutes,
        title: m.class_name,
        subtitle: m.substitute_name || m.instructor_name,
        color: SWATCH[m.colour] || "var(--accent-yellow)",
        muted: m.status === "cancelled",
        tags: [
          m.status === "cancelled" ? { label: "No class" } : null,
          m.substitute_name ? { label: "Substitute", accent: true } : null,
        ].filter(Boolean),
        onPress: () => setPicked(m),
      })),
    [shown, ownZone],
  );

  /* One meeting's save, at the revision the calendar read. A refusal (the
     term moved under the page, a clock-change gap) is said in the popup,
     which stays open; a save reloads the range, revision included. */
  const saveMeeting = useCallback(
    async (m, changes, done) => {
      try {
        await orgApi.put(`/org/classes/${m.class_id}/meetings/${m.occurrence_id}`, {
          expected_revision: m.term_revision || 0,
          ...changes,
        });
      } catch (err) {
        throw new Error(getOrgErrorMessage(err, "Couldn't change this meeting"), { cause: err });
      }
      toast.success(done);
      load();
    },
    [load],
  );

  const total = data?.meetings?.length || 0;

  /* The portal's wide page, like the dashboard and Programs: a week grid is
     seven columns of overlapping classes, and every pixel of width is room
     for a class's name. */
  return (
    <PageShell width="wide">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="mb-2 font-serif text-2xl font-semibold text-[var(--text-primary)]">Calendar</h1>
          <p className="font-sans text-[14px] text-[var(--text-tertiary)]">
            Every class your programs are teaching, coloured by program.
            {data?.timezone && (
              <span data-testid="cal-timezone">
                {" "}
                Times are {data.timezone.replaceAll("_", " ")} unless marked.
              </span>
            )}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          icon="settings"
          onPress={() => navigate(ORG_SCHEDULE_SETTINGS_PATH)}
          data-testid="cal-scheduling-preferences"
        >
          Scheduling preferences
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)}
                aria-label="Filter by program" data-testid="cal-filter-program" className={filterClass}>
          <option value="all">All programs</option>
          {(data?.programs || []).map((p) => (
            <option key={p.program_id} value={p.program_id}>{p.title}</option>
          ))}
        </select>
        <select value={instructorFilter} onChange={(e) => setInstructorFilter(e.target.value)}
                aria-label="Filter by instructor" data-testid="cal-filter-instructor" className={filterClass}>
          <option value="all">All instructors</option>
          {(data?.instructors || []).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {shown.length !== total && (
        <p className="mb-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="cal-filtered">
          Showing {shown.length} of {total} in this range.
        </p>
      )}

      {/* A connected class whose schedule did not answer makes this range
          INCOMPLETE, and an incomplete calendar that renders as a quiet one is
          worse than no calendar. Said once, above the grid, and the empty-day
          wording changes with it — "nothing scheduled" is a claim, "no meetings
          loaded" is the truth when Hans could not ask. */}
      {data?.complete === false && (
        <p role="status" className="mb-3 font-sans text-[13px] text-[var(--text-secondary)]">
          Some class schedules are unavailable. This calendar is incomplete; refresh to try again.
        </p>
      )}

      <MeetingCalendar
        events={events}
        view={view}
        onViewChange={setView}
        anchor={anchor}
        onAnchorChange={setAnchor}
        loading={loading}
        emptyText={data?.complete === false ? "No meetings loaded" : "Nothing scheduled"}
        // A day with more classes than the cell can hold opens in the week
        // grid, where they are laid out side by side rather than truncated.
        onOpenDay={(key) => {
          const [y, m, d] = key.split("-").map(Number);
          setAnchor(new Date(y, m - 1, d));
          setView("week");
        }}
      />

      <MeetingDetailsDialog
        key={picked ? `${picked.class_id}-${picked.occurrence_id || picked.session_id || picked.date}` : "none"}
        meeting={
          picked && {
            ...calendarMeeting(picked),
            tags: [
              ...calendarMeeting(picked).tags,
              ...(canCover(picked) && teachersOf(picked).length === 0 ? [{ label: "No instructor", accent: true }] : []),
            ],
          }
        }
        onClose={() => setPicked(null)}
        edit={
          picked?.editable
            ? {
                notice: "Moving a meeting doesn't notify anyone. Cancelling one tells its instructor.",
                onSave: (changes) => saveMeeting(picked, changes, "Meeting moved"),
                onCancel: () => saveMeeting(picked, { status: "cancelled" }, "Meeting cancelled"),
                onRestore: () => saveMeeting(picked, { status: "scheduled" }, "Meeting restored"),
              }
            : undefined
        }
        locked={
          picked && !picked.editable
            ? picked.session_id
              ? "This class is booked through Kalver Scheduler, so its meetings are changed there."
              : "This class's meetings are changed from its schedule, on the class page."
            : undefined
        }
        cover={
          canCover(picked)
            ? {
                lead: teachersOf(picked).length === 0,
                onPress: () => {
                  setCovering(picked);
                  setPicked(null);
                },
              }
            : undefined
        }
        open={
          picked && {
            label: "Open class",
            onPress: () =>
              navigate(
                `/org/classes/${picked.class_id}?tab=calendar${picked.occurrence_id ? `&meeting=${picked.occurrence_id}` : ""}`,
              ),
          }
        }
      />

      <AssignSubstituteDialog
        open={Boolean(covering)}
        onOpenChange={(open) => !open && setCovering(null)}
        classId={covering?.class_id}
        session={covering && { date: covering.date, time_local: covering.time_local, occurrence_id: covering.occurrence_id || null }}
        excludeUserIds={covering?.instructor_ids || (covering?.instructor_id ? [covering.instructor_id] : [])}
        onAssigned={load}
      />

      {(data?.unshown_programs || []).length > 0 && (
        <>
          <SectionHeader className="mt-6">Not shown here</SectionHeader>
          <Card data-testid="cal-unshown">
            <p className="font-sans text-[14px] text-[var(--text-secondary)]">
              {data.unshown_programs.map((p) => p.title).join(", ")}
            </p>
            <p className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">
              {data.unshown_programs.length === 1 ? "This program books" : "These programs book"} families
              directly, so {data.unshown_programs.length === 1 ? "its" : "their"} schedule lives in Kalver
              Scheduler rather than here.
            </p>
          </Card>
        </>
      )}
    </PageShell>
  );
}
